import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import type { ListLead, ListVariables } from "../types";

async function updateVariableValueRequest(
  workspaceId: string,
  listId: string,
  targetUrn: string,
  key: string,
  value: string,
) {
  //the endpoint is a bulk upsert, a single value is a one entry payload
  await post(`workspaces/${workspaceId}/lists/${listId}/custom-variables`, {
    custom_variables: [{ target_urn: targetUrn, variables: { [key]: value } }],
  });
}

async function createVariableRequest(
  workspaceId: string,
  listId: string,
  variableNames: string[],
): Promise<Pick<ListVariables, "custom_variable_keys">> {
  //the response carries the keys the request created
  return post(
    `workspaces/${workspaceId}/lists/${listId}/custom-variable-keys`,
    { variable_names: variableNames },
  );
}

export default function useLeadListVariablesMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const {
    mutateAsync: updateVariableValue,
    isPending: isUpdatingVariableValue,
  } = useMutation({
    mutationFn: ({
      listId,
      targetUrn,
      key,
      value,
    }: {
      listId: string;
      targetUrn: string;
      //the variable the value belongs to, one of the list's variable keys
      key: string;
      value: string;
    }) =>
      updateVariableValueRequest(workspaceId, listId, targetUrn, key, value),
    onSuccess: (_, { listId, targetUrn, key, value }) => {
      //the leads key leaves off the search and the paging, so every cached page
      //of the tab is patched rather than refetched
      queryClient.setQueriesData<{ people: ListLead[] }>(
        { queryKey: listsKeys.leads(listId, false) },
        (prevLeads) =>
          prevLeads && {
            ...prevLeads,
            people: prevLeads.people.map((lead) =>
              lead.urn === targetUrn
                ? {
                    ...lead,
                    custom_variables: {
                      ...lead.custom_variables,
                      [key]: value,
                    },
                  }
                : lead,
            ),
          },
      );

      queryClient.setQueryData<ListVariables>(
        listsKeys.variables(listId),
        (prevVariables) => {
          if (!prevVariables) {
            return prevVariables;
          }

          const { custom_variables: storedVariables } = prevVariables;
          const hasTarget = storedVariables.some(
            ({ target_urn }) => target_urn === targetUrn,
          );

          return {
            ...prevVariables,
            custom_variables: hasTarget
              ? storedVariables.map((storedVariable) =>
                  storedVariable.target_urn === targetUrn
                    ? {
                        ...storedVariable,
                        variables: {
                          ...storedVariable.variables,
                          [key]: value,
                        },
                      }
                    : storedVariable,
                )
              : //nothing was stored for this lead yet, the value is its first
                [
                  ...storedVariables,
                  { target_urn: targetUrn, variables: { [key]: value } },
                ],
          };
        },
      );
    },
    onError: () => toast.error("Failed to update variable"),
  });

  const { mutateAsync: createVariable, isPending: isCreatingVariable } =
    useMutation({
      mutationFn: ({
        listId,
        variableNames,
      }: {
        listId: string;
        variableNames: string[];
      }) => createVariableRequest(workspaceId, listId, variableNames),
      onSuccess: ({ custom_variable_keys: createdKeys }, { listId }) => {
        //the created keys are appended to the ones the list already has, keys
        //that are already stored are left as they are
        function appendCreatedKeys(storedKeys: string[]) {
          return [
            ...storedKeys,
            ...createdKeys.filter(
              (createdKey) => !storedKeys.includes(createdKey),
            ),
          ];
        }

        queryClient.setQueryData<ListVariables>(
          listsKeys.variables(listId),
          (prevVariables) =>
            prevVariables && {
              ...prevVariables,
              custom_variable_keys: appendCreatedKeys(
                prevVariables.custom_variable_keys,
              ),
            },
        );

        //the table's columns come from the leads-search, so every cached page
        //of the tab carries its own copy of the keys
        queryClient.setQueriesData<{ custom_variable_keys: string[] }>(
          { queryKey: listsKeys.leads(listId, false) },
          (prevLeads) =>
            prevLeads && {
              ...prevLeads,
              custom_variable_keys: appendCreatedKeys(
                prevLeads.custom_variable_keys,
              ),
            },
        );
      },
      onError: () => toast.error("Failed to create variable"),
    });

  return {
    updateVariableValue,
    isUpdatingVariableValue,
    createVariable,
    isCreatingVariable,
  };
}
